import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { authApi } from "../api/authApi";
import { Card } from "../components/layout/Card";

/**
 * Self-service password reset, step 1: ask for the e-mail address (the
 * username). The answer is always the same so it reveals nothing about
 * which accounts exist.
 */
export function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await authApi.forgotPassword(username.trim());
    } catch {
      // Deliberately the same outcome as success.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <section className="auth-shell">
      <Card>
        <div className="auth-card">
          <h2>Forgot password</h2>
          {done ? (
            <>
              <p className="sub">
                If an account exists for this e-mail address, a message with a link to choose a new password has been
                sent. Check your spam folder if it doesn't arrive within a few minutes.
              </p>
              <Link to="/login" className="btn btn-primary" style={{ textDecoration: "none" }}>
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <p className="sub">Enter your e-mail address (your username) and we'll send you a link to choose a new password.</p>
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label htmlFor="username">E-mail address</label>
                  <input
                    id="username"
                    type="email"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={submitting || !username.trim()}>
                  <Mail size={16} /> {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <p style={{ marginTop: 16, fontSize: "0.9rem" }}>
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </Card>
    </section>
  );
}
